import React, { useState, useEffect } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Container, 
  Grid, 
  Paper, 
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Tooltip,
  ListItemButton
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Public as PublicIcon,
  LibraryBooks as LibraryBooksIcon,
  Category as CategoryIcon,
  Attribution as AttributionIcon,
  Inventory2 as Inventory2Icon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Star as StarIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  SupportAgent as SupportAgentIcon,
  PointOfSale as POSIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 1,
}));

const InventoryDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [language, setLanguage] = useState('bn'); // 'bn' for Bangla, 'en' for English
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [inventoryData, setInventoryData] = useState({
    totalProducts: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    totalValue: 0,
    recentProducts: [],
    categories: [],
    stockUtilization: 0
  });

  useEffect(() => {
    loadInventoryData();
  }, []);

  const loadInventoryData = () => {
    // Mock data - replace with actual API calls
    setInventoryData({
      totalProducts: 1250,
      lowStockItems: 45,
      outOfStockItems: 12,
      totalValue: 2500000,
      stockUtilization: 78,
      categories: [
        { name: 'খাদ্যশস্য', count: 320, color: '#4CAF50' },
        { name: 'ডাল', count: 180, color: '#FF9800' },
        { name: 'মসলা', count: 95, color: '#F44336' },
        { name: 'তেল', count: 75, color: '#9C27B0' }
      ],
      recentProducts: [
        {
          id: 1,
          name: 'বাসমতি চাল',
          sku: 'RICE-001',
          stockLevel: 150,
          reorderPoint: 50,
          status: 'in_stock',
          category: 'খাদ্যশস্য',
          price: 120,
          unit: 'কেজি'
        },
        {
          id: 2,
          name: 'মসুর ডাল',
          sku: 'DAL-002',
          stockLevel: 25,
          reorderPoint: 30,
          status: 'low_stock',
          category: 'ডাল',
          price: 180,
          unit: 'কেজি'
        },
        {
          id: 3,
          name: 'চিনি',
          sku: 'SUGAR-003',
          stockLevel: 0,
          reorderPoint: 20,
          status: 'out_of_stock',
          category: 'খাদ্যশস্য',
          price: 95,
          unit: 'কেজি'
        },
        {
          id: 4,
          name: 'হলুদ গুঁড়ো',
          sku: 'SPICE-004',
          stockLevel: 45,
          reorderPoint: 25,
          status: 'in_stock',
          category: 'মসলা',
          price: 220,
          unit: 'কেজি'
        }
      ]
    });
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    handleLanguageMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    // In a real app, this would handle logout logic
    navigate('/login');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    navigate(menuItems[newValue].path);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleMobileTabChange = (newValue) => {
    setCurrentTab(newValue);
    setMobileDrawerOpen(false);
  };

  const handleAddProduct = () => {
    console.log('Add product clicked');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'success';
      case 'low_stock': return 'warning';
      case 'out_of_stock': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in_stock': return 'স্টকে আছে';
      case 'low_stock': return 'কম স্টক';
      case 'out_of_stock': return 'স্টক নেই';
      default: return 'অজানা';
    }
  };

  // Translations
  const translations = {
    bn: {
      title: 'ইনভেন্টরি ড্যাশবোর্ড',
      dashboard: 'ড্যাশবোর্ড',
      products: 'পণ্যসমূহ',
      posIntegration: 'POS ইন্টিগ্রেশন',
      analytics: 'বিশ্লেষণ',
      settings: 'সেটিংস',
      profile: 'প্রোফাইল',
      logout: 'লগআউট',
      addProducts: 'পণ্য যোগ করুন',
      viewProducts: 'পণ্য দেখুন',
      posSystem: 'POS সিস্টেম',
      viewAnalytics: 'বিশ্লেষণ দেখুন',
      language: 'ভাষা',
      bangla: 'বাংলা',
      english: 'English'
    },
    en: {
      title: 'Inventory Dashboard',
      dashboard: 'Dashboard',
      products: 'Products',
      posIntegration: 'POS Integration',
      analytics: 'Analytics',
      settings: 'Settings',
      profile: 'Profile',
      logout: 'Logout',
      addProducts: 'Add Products',
      viewProducts: 'View Products',
      posSystem: 'POS System',
      viewAnalytics: 'View Analytics',
      language: 'Language',
      bangla: 'বাংলা',
      english: 'English'
    }
  };

  const t = translations[language];

  const menuItems = [
    { text: t.dashboard, icon: <DashboardIcon />, path: '/inventory/dashboard' },
    { text: t.products, icon: <InventoryIcon />, path: '/inventory/products' },
    { text: t.posIntegration, icon: <POSIcon />, path: '/inventory/pos-system' },
    { text: t.analytics, icon: <BarChartIcon />, path: '/inventory/analytics' },
    { text: t.settings, icon: <SettingsIcon />, path: '/inventory/settings' },
  ];

  // Set current tab based on current path
  React.useEffect(() => {
    const currentIndex = menuItems.findIndex(item => item.path === location.pathname);
    if (currentIndex !== -1) {
      setCurrentTab(currentIndex);
    }
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <AppBarStyled>
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {t.title}
          </Typography>
          
          {/* Quick Actions */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            gap: 1, 
            mr: 2 
          }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<POSIcon />}
              component={Link}
              to="/inventory/pos-system"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              POS সিস্টেম
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              পণ্য যোগ করুন
            </Button>
          </Box>
          
          {/* Language Switcher */}
          <IconButton 
            color="inherit" 
            onClick={handleLanguageMenuOpen}
            sx={{ mr: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          
          {/* View Site Icon */}
          <IconButton 
            color="inherit" 
            component="a" 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ mr: 1 }}
          >
            <PublicIcon />
          </IconButton>
          
          <IconButton color="inherit">
            <Badge badgeContent={3} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>I</Avatar>
          </IconButton>
          
          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            disableScrollLock={true}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t.profile}
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t.settings}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t.logout}
            </MenuItem>
          </Menu>
          
          {/* Language Menu */}
          <Menu
            anchorEl={languageMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            disableScrollLock={true}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('bn')}
              selected={language === 'bn'}
            >
              {t.bangla}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
            >
              {t.english}
            </MenuItem>
          </Menu>
        </Toolbar>
        
        {/* Navigation Tabs - Hidden on Mobile */}
        {!isMobile && (
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'rgba(255,255,255,0.2)',
            background: 'linear-gradient(45deg, #3d1789 30%, #0a6abd 90%)'
          }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: 'white' }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.8)',
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Tab 
                  key={item.text}
                  label={item.text} 
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ minWidth: 120 }}
                />
              ))}
            </Tabs>
          </Box>
        )}
      </AppBarStyled>

      {/* Mobile Navigation Drawer */}
      <SwipeableDrawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerClose}
        onOpen={() => setMobileDrawerOpen(true)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'white',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t.title}
            </Typography>
            <IconButton onClick={handleMobileDrawerClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        <List component="nav" sx={{ p: 1 }}>
          {menuItems.map((item, index) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleMobileNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Quick Actions in Mobile Drawer */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            দ্রুত কার্যক্রম
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<POSIcon />}
              fullWidth
              component={Link}
              to="/inventory/pos-system"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              POS সিস্টেম
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              fullWidth
              component={Link}
              to="/inventory/products/add"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              পণ্য যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<InventoryIcon />}
              fullWidth
              component={Link}
              to="/inventory/products"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              পণ্য দেখুন
            </Button>
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="menu"
          onClick={handleMobileDrawerToggle}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            display: { xs: 'flex', md: 'none' },
            background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)'
          }}
        >
          <MenuIcon />
        </Fab>
      )}
      
      {/* Main Content Area with Sidebar */}
      <Box sx={{ display: 'flex', pt: { xs: 8, md: 16 } }}>
        {/* Left Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              backgroundColor: 'white',
              borderRight: '1px solid #e0e0e0',
              minHeight: 'calc(100vh - 128px)',
              p: 2,
              position: 'sticky',
              top: 128,
            }}
          >
          {/* Quick Actions */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            দ্রুত কার্যক্রম
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              onClick={handleAddProduct}
              sx={{ justifyContent: 'flex-start' }}
            >
              পণ্য যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<POSIcon />}
              fullWidth
              component={Link}
              to="/inventory/pos-system"
              sx={{ justifyContent: 'flex-start' }}
            >
              POS সিস্টেম
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<BarChartIcon />}
              fullWidth
              component={Link}
              to="/inventory/analytics"
              sx={{ justifyContent: 'flex-start' }}
            >
              বিশ্লেষণ দেখুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<InventoryIcon />}
              fullWidth
              component={Link}
              to="/inventory/products"
              sx={{ justifyContent: 'flex-start' }}
            >
              সব পণ্য দেখুন
            </Button>
          </Box>
          
          {/* Quick Stats */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            আজকের পরিসংখ্যান
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                মোট পণ্য
              </Typography>
              <Typography variant="h4" color="primary">
                {inventoryData.totalProducts.toLocaleString()}
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                মোট মূল্য
              </Typography>
              <Typography variant="h4" color="success.main">
                ৳{(inventoryData.totalValue / 100000).toFixed(1)}L
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                কম স্টক আইটেম
              </Typography>
              <Typography variant="h4" color="warning.main">
                {inventoryData.lowStockItems}
              </Typography>
            </Paper>
          </Box>
          
          {/* Recent Notifications */}
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 3, 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            সাম্প্রতিক বিজ্ঞপ্তি
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                কম স্টক সতর্কতা
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ৫ মিনিট আগে
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#ffebee' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                স্টক নেই - চিনি
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ১৫ মিনিট আগে
              </Typography>
            </Paper>
          </Box>
        </Box>
        )}
        
        {/* Main Content */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            minHeight: 'calc(100vh - 128px)',
            p: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/products" element={<div>Products Page</div>} />
            <Route path="/pos-system" element={<div>POS System Page</div>} />
            <Route path="/analytics" element={<div>Analytics Page</div>} />
            <Route path="/settings" element={<div>Settings Page</div>} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

// Default dashboard home component
const Dashboard = () => {
  const [inventoryData, setInventoryData] = useState({
    totalProducts: 1250,
    lowStockItems: 45,
    outOfStockItems: 12,
    totalValue: 2500000,
    stockUtilization: 78,
    categories: [
      { name: 'খাদ্যশস্য', count: 320, color: '#4CAF50' },
      { name: 'ডাল', count: 180, color: '#FF9800' },
      { name: 'মসলা', count: 95, color: '#F44336' },
      { name: 'তেল', count: 75, color: '#9C27B0' }
    ],
    recentProducts: [
      {
        id: 1,
        name: 'বাসমতি চাল',
        sku: 'RICE-001',
        stockLevel: 150,
        reorderPoint: 50,
        status: 'in_stock',
        category: 'খাদ্যশস্য',
        price: 120,
        unit: 'কেজি'
      },
      {
        id: 2,
        name: 'মসুর ডাল',
        sku: 'DAL-002',
        stockLevel: 25,
        reorderPoint: 30,
        status: 'low_stock',
        category: 'ডাল',
        price: 180,
        unit: 'কেজি'
      },
      {
        id: 3,
        name: 'চিনি',
        sku: 'SUGAR-003',
        stockLevel: 0,
        reorderPoint: 20,
        status: 'out_of_stock',
        category: 'খাদ্যশস্য',
        price: 95,
        unit: 'কেজি'
      },
      {
        id: 4,
        name: 'হলুদ গুঁড়ো',
        sku: 'SPICE-004',
        stockLevel: 45,
        reorderPoint: 25,
        status: 'in_stock',
        category: 'মসলা',
        price: 220,
        unit: 'কেজি'
      }
    ]
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'success';
      case 'low_stock': return 'warning';
      case 'out_of_stock': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in_stock': return 'স্টকে আছে';
      case 'low_stock': return 'কম স্টক';
      case 'out_of_stock': return 'স্টক নেই';
      default: return 'অজানা';
    }
  };

  return (
    <Box>
      {/* First Div - Statistics Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          {/* Enhanced Statistics Cards */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <InventoryIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মোট পণ্য
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {inventoryData.totalProducts.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মোট মূল্য
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    ৳{(inventoryData.totalValue / 100000).toFixed(1)}L
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <WarningIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    কম স্টক আইটেম
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {inventoryData.lowStockItems}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <LocalShippingIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    স্টক নেই
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {inventoryData.outOfStockItems}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <CategoryIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    পণ্য বিভাগ
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {inventoryData.categories.length}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    স্টক ব্যবহার
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {inventoryData.stockUtilization}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Second Div - Main Content Sections */}
      <Box>
        <Grid container spacing={2}>
          {/* Recent Products */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  সাম্প্রতিক পণ্য
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<AddIcon />}
                >
                  নতুন পণ্য
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>পণ্যের নাম</TableCell>
                      <TableCell align="right">স্টক</TableCell>
                      <TableCell align="right">মূল্য</TableCell>
                      <TableCell align="right">অবস্থা</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryData.recentProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {product.category}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {product.stockLevel} {product.unit}
                        </TableCell>
                        <TableCell align="right">৳{product.price}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={getStatusText(product.status)} 
                            color={getStatusColor(product.status)}
                            size="small" 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Categories Breakdown */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  পণ্য বিভাগ
                </Typography>
                <Button variant="outlined" size="small">
                  সব দেখুন
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>বিভাগ</TableCell>
                      <TableCell align="right">পণ্য সংখ্যা</TableCell>
                      <TableCell align="right">শতকরা হার</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryData.categories.map((category) => (
                      <TableRow key={category.name}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                backgroundColor: category.color,
                                mr: 2
                              }} 
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {category.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{category.count}</TableCell>
                        <TableCell align="right">
                          {((category.count / inventoryData.totalProducts) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Low Stock Alert */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  কম স্টক সতর্কতা
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<POSIcon />}
                  component={Link}
                  to="/inventory/pos-system"
                >
                  POS সিস্টেম
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        মসুর ডাল
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        মাত্র ২৫ কেজি বাকি
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="warning">
                      স্টক করুন
                    </Button>
                  </Box>
                </Paper>
                
                <Paper sx={{ p: 2, bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        চিনি
                      </Typography>
                      <Typography variant="body2" color="error.main">
                        স্টক নেই
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="error">
                      স্টক করুন
                    </Button>
                  </Box>
                </Paper>
                
                <Paper sx={{ p: 2, bgcolor: '#e8f5e8', border: '1px solid #4caf50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        হলুদ গুঁড়ো
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        মাত্র ৪৫ কেজি বাকি
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="success">
                      স্টক করুন
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default InventoryDashboard; 